/**
 * AuditLogsPage - Admin page for viewing system audit logs
 */

import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/shared/lib/api";
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge } from "@/shared/components/ui";
import { formatDateTime } from "@/shared/lib/utils";
import {
  History,
  Filter,
  Download,
  ChevronRight,
  Loader2,
  AlertCircle,
  User,
  Shield,
  Server,
  FileText,
  MessageSquare,
  CreditCard,
  RefreshCw,
} from "lucide-react";
import type { AuditLog, AuditAction } from "@/shared/types";

const ACTION_LABELS: Record<AuditAction, string> = {
  "advisor.registered": "Rejestracja doradcy",
  "advisor.logged_in": "Logowanie doradcy",
  "advisor.profile_updated": "Aktualizacja profilu",
  "advisor.verified": "Weryfikacja doradcy",
  "advisor.suspended": "Zawieszenie doradcy",
  "advisor.credits_assigned": "Przydzielenie kredytów",
  "lead.reserved": "Rezerwacja leada",
  "session.started": "Rozpoczęcie sesji",
  "session.updated": "Aktualizacja sesji",
  "session.completed": "Zakończenie sesji",
  "session.consent_granted": "Udzielenie zgody",
  "session.consent_revoked": "Cofnięcie zgody",
  "session.data_updated": "Aktualizacja danych klienta",
  "offer.created": "Utworzenie oferty",
  "offer.updated": "Aktualizacja oferty",
  "offer.deleted": "Usunięcie oferty",
  "admin.logged_in": "Logowanie admina",
};

const ACTION_CATEGORIES: Record<string, AuditAction[]> = {
  Doradcy: ["advisor.registered", "advisor.logged_in", "advisor.profile_updated", "advisor.verified", "advisor.suspended", "advisor.credits_assigned"],
  Leady: ["lead.reserved"],
  Sesje: ["session.started", "session.updated", "session.completed", "session.consent_granted", "session.consent_revoked", "session.data_updated"],
  Oferty: ["offer.created", "offer.updated", "offer.deleted"],
  Admin: ["admin.logged_in"],
};

function getActionIcon(action: AuditAction) {
  if (action.startsWith("advisor.")) return User;
  if (action.startsWith("lead.")) return FileText;
  if (action.startsWith("session.")) return MessageSquare;
  if (action.startsWith("offer.")) return CreditCard;
  if (action.startsWith("admin.")) return Shield;
  return Server;
}

function getActorTypeIcon(actorType: string) {
  switch (actorType) {
    case "advisor":
      return User;
    case "admin":
      return Shield;
    case "system":
      return Server;
    default:
      return User;
  }
}

function getActionColor(action: AuditAction): "default" | "info" | "success" | "warning" | "error" {
  if (action.includes("created") || action.includes("registered")) return "success";
  if (action.includes("deleted") || action.includes("suspended")) return "error";
  if (action.includes("updated") || action.includes("assigned")) return "info";
  if (action.includes("verified")) return "success";
  return "default";
}

export function AuditLogsPage() {
  const [filters, setFilters] = useState({
    action: "",
    actorType: "" as "" | "advisor" | "admin" | "system",
    startDate: "",
    endDate: "",
  });

  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["audit-logs", filters],
    queryFn: () => api.getAuditLogs({
      action: filters.action || undefined,
      actorType: filters.actorType || undefined,
      startDate: filters.startDate || undefined,
      endDate: filters.endDate || undefined,
      limit: 100,
    }),
  });

  const logs = (data?.logs || []) as AuditLog[];

  const handleExportCSV = () => {
    const headers = ["Timestamp", "Action", "Actor", "Actor Type", "Target", "IP Address"];
    const rows = logs.map((log) => [
      log.timestamp,
      ACTION_LABELS[log.action] || log.action,
      log.actorName || log.actorId,
      log.actorType,
      log.targetId || "-",
      log.ipAddress || "-",
    ]);

    const csvContent = [headers, ...rows]
      .map((row) => row.map((cell) => `"${cell}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split("T")[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearFilters = () => {
    setFilters({
      action: "",
      actorType: "",
      startDate: "",
      endDate: "",
    });
  };

  const hasActiveFilters = filters.action || filters.actorType || filters.startDate || filters.endDate;

  if (error) {
    return (
      <div className="p-6 text-center">
        <AlertCircle className="w-12 h-12 text-error-500 mx-auto mb-4" />
        <p className="text-gray-600">Nie udalo sie pobrac logow audytu</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <History className="w-6 h-6" />
            Historia Dzialań
          </h1>
          <p className="text-gray-600 mt-1">
            Przegladaj wszystkie akcje wykonane w systemie
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            leftIcon={<RefreshCw className="w-4 h-4" />}
          >
            Odswiez
          </Button>
          <Button
            variant={showFilters ? "primary" : "outline"}
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<Filter className="w-4 h-4" />}
          >
            Filtry {hasActiveFilters && "(aktywne)"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleExportCSV}
            leftIcon={<Download className="w-4 h-4" />}
            disabled={logs.length === 0}
          >
            Eksport CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typ akcji
                </label>
                <select
                  value={filters.action}
                  onChange={(e) => setFilters({ ...filters, action: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Wszystkie</option>
                  {Object.entries(ACTION_CATEGORIES).map(([category, actions]) => (
                    <optgroup key={category} label={category}>
                      {actions.map((action) => (
                        <option key={action} value={action}>
                          {ACTION_LABELS[action]}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Typ aktora
                </label>
                <select
                  value={filters.actorType}
                  onChange={(e) => setFilters({ ...filters, actorType: e.target.value as "" | "advisor" | "admin" | "system" })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">Wszyscy</option>
                  <option value="advisor">Doradca</option>
                  <option value="admin">Administrator</option>
                  <option value="system">System</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data od
                </label>
                <Input
                  type="datetime-local"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data do
                </label>
                <Input
                  type="datetime-local"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
              </div>
            </div>

            {hasActiveFilters && (
              <div className="mt-4 flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  Wyczysc filtry
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Logs List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Logi ({data?.count || 0})</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <History className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Brak logow do wyswietlenia</p>
              {hasActiveFilters && (
                <p className="text-sm mt-2">Sprobuj zmienić filtry</p>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {logs.map((log) => {
                const ActionIcon = getActionIcon(log.action);
                const ActorIcon = getActorTypeIcon(log.actorType);

                return (
                  <div
                    key={log.id}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start gap-4">
                      {/* Icon */}
                      <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <ActionIcon className="w-5 h-5 text-gray-600" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge variant={getActionColor(log.action)}>
                            {ACTION_LABELS[log.action] || log.action}
                          </Badge>
                          {log.targetType && log.targetId && (
                            <span className="text-sm text-gray-500">
                              <ChevronRight className="w-4 h-4 inline" />
                              {log.targetType}: {log.targetId.substring(0, 8)}...
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-600">
                          <ActorIcon className="w-4 h-4" />
                          <span className="font-medium">{log.actorName || log.actorId}</span>
                          <span className="text-gray-400">|</span>
                          <span>{formatDateTime(log.timestamp)}</span>
                          {log.ipAddress && (
                            <>
                              <span className="text-gray-400">|</span>
                              <span className="text-gray-400">{log.ipAddress}</span>
                            </>
                          )}
                        </div>

                        {/* Metadata */}
                        {log.metadata && Object.keys(log.metadata).length > 0 && (
                          <div className="mt-2 text-xs text-gray-500 bg-gray-50 rounded p-2 font-mono">
                            {Object.entries(log.metadata).map(([key, value]) => (
                              <div key={key}>
                                <span className="text-gray-400">{key}:</span>{" "}
                                {typeof value === "object" ? JSON.stringify(value) : String(value)}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
